import { useState, useEffect, useRef } from 'react'
import './Calendario.css'

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

const aISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const deISO = (s) => { if (!s) return null; const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
const hoy0 = () => { const h = new Date(); h.setHours(0, 0, 0, 0); return h }

/**
 * Selector de fecha con la estética de la web.
 * - value / onChange trabajan con 'YYYY-MM-DD' (igual que un <input type="date">)
 * - diasCerrados: días de la semana deshabilitados (0 = domingo … 6 = sábado)
 * - no permite fechas anteriores a hoy
 */
export default function Calendario({ value, onChange, diasCerrados = [0], placeholder = 'Elige un día' }) {
  const [abierto, setAbierto] = useState(false)
  const seleccionado = deISO(value)
  const [vista, setVista] = useState(() => { const d = seleccionado || new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })
  const ref = useRef(null)

  // Cerrar al hacer clic fuera o pulsar Escape
  useEffect(() => {
    if (!abierto) return
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false) }
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [abierto])

  const hoy = hoy0()
  const mesAnterior = () => setVista(v => new Date(v.getFullYear(), v.getMonth() - 1, 1))
  const mesSiguiente = () => setVista(v => new Date(v.getFullYear(), v.getMonth() + 1, 1))
  const puedeIrAtras = vista > new Date(hoy.getFullYear(), hoy.getMonth(), 1)

  // Rejilla del mes (empezando en lunes)
  const primerDia = new Date(vista.getFullYear(), vista.getMonth(), 1)
  const desplazamiento = (primerDia.getDay() + 6) % 7   // lunes = 0
  const diasEnMes = new Date(vista.getFullYear(), vista.getMonth() + 1, 0).getDate()
  const celdas = []
  for (let i = 0; i < desplazamiento; i++) celdas.push(null)
  for (let d = 1; d <= diasEnMes; d++) celdas.push(new Date(vista.getFullYear(), vista.getMonth(), d))
  while (celdas.length % 7 !== 0) celdas.push(null)

  const elegir = (d) => { onChange(aISO(d)); setAbierto(false) }

  const etiqueta = seleccionado
    ? seleccionado.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).replace(/^./, c => c.toUpperCase())
    : placeholder

  return (
    <div className="cal" ref={ref}>
      <button type="button" className={`cal-input ${seleccionado ? 'con-valor' : ''}`} onClick={() => setAbierto(a => !a)} aria-haspopup="dialog" aria-expanded={abierto}>
        <span>{etiqueta}</span>
        <span className="cal-icono" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="5" width="18" height="16" rx="1"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>
        </span>
      </button>

      {abierto && (
        <div className="cal-popover" role="dialog" aria-label="Elegir fecha">
          <div className="cal-cabecera">
            <button type="button" className="cal-nav" onClick={mesAnterior} disabled={!puedeIrAtras} aria-label="Mes anterior">‹</button>
            <div className="cal-mes">{MESES[vista.getMonth()]} <span>{vista.getFullYear()}</span></div>
            <button type="button" className="cal-nav" onClick={mesSiguiente} aria-label="Mes siguiente">›</button>
          </div>
          <div className="cal-semana">
            {DIAS.map(d => <div key={d} className="cal-dia-nombre">{d}</div>)}
          </div>
          <div className="cal-rejilla">
            {celdas.map((d, i) => {
              if (!d) return <div key={`v${i}`} className="cal-celda vacia" />
              const pasado = d < hoy
              const cerrado = diasCerrados.includes(d.getDay())
              const deshabilitado = pasado || cerrado
              const esHoy = d.getTime() === hoy.getTime()
              const activo = seleccionado && d.getTime() === seleccionado.getTime()
              return (
                <button
                  type="button"
                  key={aISO(d)}
                  className={`cal-celda ${deshabilitado ? 'off' : ''} ${cerrado && !pasado ? 'cerrado' : ''} ${esHoy ? 'hoy' : ''} ${activo ? 'activo' : ''}`}
                  disabled={deshabilitado}
                  onClick={() => elegir(d)}
                  title={cerrado ? 'Cerrado' : undefined}
                >
                  {d.getDate()}
                </button>
              )
            })}
          </div>
          <div className="cal-pie">
            <span><i className="cal-leyenda cerrado" /> Cerrado</span>
            {seleccionado && <button type="button" className="cal-borrar" onClick={() => { onChange(''); setAbierto(false) }}>Borrar</button>}
          </div>
        </div>
      )}
    </div>
  )
}
